/**
 * Structural interfaces for the injected shared-package instances.
 *
 * We rely on the FROZEN public API surface of the shared packages, but type the
 * dependencies structurally here so the app is decoupled from the exact exported
 * type *names* (which are produced by sibling agents). Any instance that matches
 * these shapes — the real `createClient`/`createAuth`/`createAnalytics` results —
 * is assignable.
 */

export interface GqlClient {
	query<T = unknown>(document: string, variables?: Record<string, unknown>): Promise<T>;
	mutate<T = unknown>(document: string, variables?: Record<string, unknown>): Promise<T>;
}

export interface Analytics {
	track(event: { type: string } & Record<string, unknown>): void;
}

/** Outcome of a sign-in attempt, as the panel needs to word it. */
export type SignInResult = "ok" | "invalid" | "rate_limited" | "unavailable";

export interface AuthUser {
	email: string;
	name?: string;
}

/**
 * Editor session. Identity is the Strapi admin account; the gateway keeps the
 * session in an HttpOnly cookie, so nothing here ever holds a token.
 */
export interface Auth {
	/** Ask the gateway who the current cookie belongs to. */
	refresh(): Promise<void>;
	signIn(email: string, password: string): Promise<SignInResult>;
	signOut(): Promise<void>;
	isAuthenticated(): boolean;
	getRoles(): string[];
	getUser(): AuthUser | null;
}

/**
 * Roles (Strapi admin role codes) that may view draft/preview content. Must match
 * EDITOR_ROLES in apps/backend/src/context.ts — the gateway is what enforces it.
 */
export const EDITOR_ROLES = [
	"group_admin",
	"domain_admin",
	"domain_editor",
	"content_approver",
] as const;
